/* Administration plateforme — réservé au personnel Fonbex (founder + platform_admin).
 *
 * Trois onglets stricts :
 *   1. Vue d'ensemble (KPIs plateforme, sans donnée patrimoniale sensible)
 *   2. Organisations clientes (recherche + consultation en lecture seule avec motif obligatoire)
 *   3. Journal d'audit (traçabilité totale des accès administrateurs)
 *
 * L'ouverture d'une organisation nécessite un motif (>= 8 caractères) qui est
 * journalisé côté backend AVANT toute lecture. L'écran affiche des données
 * en lecture seule uniquement.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield, Users, Building2, ClipboardList, Search, Eye, AlertTriangle,
  ShieldCheck, RefreshCw, Mail, CheckCircle2, XCircle, Sparkles, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const allowed = ["founder", "platform_admin"].includes(user?.platform_role);
  if (!allowed) return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="animate-fade-up" data-testid="platform-admin-page">
      <PageHeader
        overline="Personnel Fonbex"
        title="Administration de la plateforme"
        description="Consultation supervisée des organisations clientes, journalisée intégralement. Aucune modification n'est possible depuis cet écran."
      />

      <div className="mb-4 rounded-2xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
        <div>
          <strong>Traçabilité complète</strong> — Chaque accès à une organisation cliente
          exige un motif écrit (min. 8 caractères) et est enregistré dans le journal d&apos;audit
          avant toute lecture. Vous consultez ici en tant que <em>{user?.platform_role}</em>.
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="pa-tab-overview">
            <Shield className="h-3.5 w-3.5 mr-1.5" />Vue d&apos;ensemble
          </TabsTrigger>
          <TabsTrigger value="orgs" data-testid="pa-tab-orgs">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />Organisations
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="pa-tab-audit">
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />Journal d&apos;audit
          </TabsTrigger>
          <TabsTrigger value="diagnostics" data-testid="pa-tab-diag">
            <Mail className="h-3.5 w-3.5 mr-1.5" />Diagnostics
          </TabsTrigger>
          <TabsTrigger value="beta" data-testid="pa-tab-beta">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Programme Bêta
          </TabsTrigger>
          <TabsTrigger value="offers" data-testid="pa-tab-offers">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Offres commerciales
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="orgs"><OrganizationsTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="diagnostics"><DiagnosticsTab /></TabsContent>
        <TabsContent value="beta"><BetaTab /></TabsContent>
        <TabsContent value="offers"><OffersTab /></TabsContent>
      </Tabs>
    </div>
  );
}

export function OverviewTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/platform-admin/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-muted-foreground">Chargement…</div>;
  return (
    <div className="space-y-6">
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Utilisateurs</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard name="pa-users-total" label="Utilisateurs" value={data.users.total} icon={Users} />
          <KpiCard name="pa-users-verified" label="E-mails vérifiés" value={data.users.verified} />
          <KpiCard name="pa-users-staff" label="Personnel Fonbex" value={data.users.staff} tone="accent" />
          <KpiCard name="pa-users-new" label="Nouveaux (7j)" value={data.users.new_7d} />
        </div>
      </section>
      <section>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Organisations</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard name="pa-orgs-total" label="Organisations" value={data.organizations.total} icon={Building2} />
          <KpiCard name="pa-orgs-real" label="Clients réels" value={data.organizations.real} tone="success" />
          <KpiCard name="pa-orgs-demo" label="Comptes démo" value={data.organizations.demo} />
          <KpiCard name="pa-orgs-new" label="Nouvelles (7j)" value={data.organizations.new_7d} />
        </div>
      </section>
    </div>
  );
}

export function OrganizationsTab() {
  const [q, setQ] = useState("");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform-admin/organizations", {
        params: { q: q || undefined, include_demo: includeDemo, limit: 100 },
      });
      setOrgs(data.items);
    } catch (e) { toast.error("Impossible de charger les organisations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [includeDemo]);

  const askReason = (org) => {
    setSelected(org);
    setReason("");
    setDetail(null);
    setReasonOpen(true);
  };

  const consult = async () => {
    if (reason.trim().length < 8) {
      toast.error("Le motif doit contenir au moins 8 caractères");
      return;
    }
    try {
      const { data } = await api.post(
        `/platform-admin/organizations/${selected.id}/consult`,
        { reason: reason.trim() },
      );
      setDetail(data);
      toast.success("Accès journalisé — consultation en lecture seule");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Consultation refusée");
    }
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="card-elevated p-4 flex flex-wrap gap-3 items-end"
      >
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs">Recherche</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Nom, slug, email propriétaire…"
                   className="pl-9" data-testid="pa-orgs-search" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={includeDemo}
                 onChange={(e) => setIncludeDemo(e.target.checked)}
                 data-testid="pa-orgs-include-demo" />
          Inclure les comptes démo
        </label>
        <Button type="submit" disabled={loading} data-testid="pa-orgs-search-btn">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
          Rechercher
        </Button>
      </form>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Propriétaire</th>
              <th className="px-3 py-2">Formule</th>
              <th className="px-3 py-2 text-right">Immeubles</th>
              <th className="px-3 py-2 text-right">Locataires</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orgs.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                Aucune organisation correspondante.
              </td></tr>
            )}
            {orgs.map((o) => (
              <tr key={o.id} data-testid={`pa-org-row-${o.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium flex items-center gap-2">
                    {o.name}
                    {o.is_demo && <StatusBadge tone="warning">Démo</StatusBadge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{o.slug}</div>
                </td>
                <td className="px-3 py-2">
                  <div>{o.owner?.email || <span className="text-muted-foreground">—</span>}</div>
                  <div className="text-xs text-muted-foreground">
                    {o.owner ? `${o.owner.first_name || ""} ${o.owner.last_name || ""}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <StatusBadge tone={o.subscription_status === "trial" ? "info" : "success"}>
                    {o.subscription_plan || "—"}
                  </StatusBadge>
                </td>
                <td className="px-3 py-2 text-right font-mono">{o.counts.buildings}</td>
                <td className="px-3 py-2 text-right font-mono">{o.counts.tenants}</td>
                <td className="px-3 py-2">
                  <Button size="sm" variant="outline" onClick={() => askReason(o)}
                          data-testid={`pa-org-consult-${o.id}`}>
                    <Eye className="h-3 w-3 mr-1" />Consulter
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={reasonOpen} onOpenChange={(o) => { setReasonOpen(o); if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Consultation supervisée — {selected?.name}
            </DialogTitle>
            <DialogDescription>
              Cette action sera journalisée avec votre e-mail, votre IP, la date, l&apos;organisation ciblée
              et le motif ci-dessous. Aucune modification n&apos;est possible.
            </DialogDescription>
          </DialogHeader>
          {!detail ? (
            <>
              <div>
                <Label>Motif de la consultation (obligatoire, min. 8 caractères)</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                          placeholder="Ex : Demande du client — ticket support #142. Vérification isolation locataires."
                          data-testid="pa-consult-reason" />
                <div className="text-xs text-muted-foreground mt-1">
                  {reason.trim().length} / 500 caractères — minimum requis : 8
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setReasonOpen(false)}>Annuler</Button>
                <Button onClick={consult} disabled={reason.trim().length < 8}
                        data-testid="pa-consult-confirm">
                  Consulter avec motif
                </Button>
              </DialogFooter>
            </>
          ) : (
            <ConsultResult detail={detail} onClose={() => setReasonOpen(false)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConsultResult({ detail, onClose }) {
  return (
    <div className="max-h-[70vh] overflow-y-auto space-y-4">
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
        <strong>Accès journalisé</strong> — Audit ID : <code className="font-mono">{detail.audit_id}</code>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(detail.counts).map(([k, v]) => (
          <div key={k} className="rounded-xl bg-secondary/40 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
            <div className="font-mono font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>
      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Propriétaire</div>
        {detail.owner ? (
          <div className="text-sm">
            {detail.owner.first_name} {detail.owner.last_name} — <span className="font-mono">{detail.owner.email}</span>
          </div>
        ) : <div className="text-sm text-muted-foreground">Aucun propriétaire</div>}
      </section>
      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Membres actifs</div>
        <div className="rounded-xl border overflow-hidden text-sm">
          {detail.members.length === 0 && <div className="p-3 text-muted-foreground">Aucun</div>}
          {detail.members.map((m, i) => (
            <div key={m.user_id + i} className="px-3 py-2 flex justify-between border-b last:border-b-0">
              <span>{m.first_name} {m.last_name} — <span className="font-mono text-xs">{m.email}</span></span>
              <StatusBadge tone="neutral">{m.role}</StatusBadge>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Sociétés ({detail.companies.length})
        </div>
        <div className="rounded-xl border overflow-hidden text-sm">
          {detail.companies.length === 0 && <div className="p-3 text-muted-foreground">Aucune société</div>}
          {detail.companies.slice(0, 20).map((c) => (
            <div key={c.id} className="px-3 py-2 flex justify-between border-b last:border-b-0">
              <span>{c.denomination || c.name} <span className="text-xs text-muted-foreground">— {c.siren || "sans SIREN"}</span></span>
              <StatusBadge tone="neutral">{c.legal_form || "—"}</StatusBadge>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Immeubles ({detail.buildings.length})
        </div>
        <div className="rounded-xl border overflow-hidden text-sm">
          {detail.buildings.length === 0 && <div className="p-3 text-muted-foreground">Aucun immeuble</div>}
          {detail.buildings.slice(0, 20).map((b) => (
            <div key={b.id} className="px-3 py-2 border-b last:border-b-0">
              <div className="font-medium">{b.name}</div>
              <div className="text-xs text-muted-foreground">{b.address}, {b.city}</div>
            </div>
          ))}
        </div>
      </section>
      <div className="text-xs text-muted-foreground italic">{detail.notice}</div>
      <DialogFooter>
        <Button onClick={onClose}>Fermer</Button>
      </DialogFooter>
    </div>
  );
}

export function AuditTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/platform-admin/audit-log", { params: { limit: 200 } })
    .then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => {
    if (!filter) return items;
    const f = filter.toLowerCase();
    return items.filter((i) =>
      (i.action || "").toLowerCase().includes(f)
      || (i.staff_email || "").toLowerCase().includes(f)
      || (i.reason || "").toLowerCase().includes(f)
    );
  }, [items, filter]);
  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-center">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Filtrer par action, e-mail, motif…" className="max-w-md"
               data-testid="pa-audit-filter" />
        <Button variant="outline" onClick={load} data-testid="pa-audit-refresh">
          <RefreshCw className="h-3 w-3 mr-1" />Rafraîchir
        </Button>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2">Horodatage</th>
              <th className="px-3 py-2">Personnel</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Organisation ciblée</th>
              <th className="px-3 py-2">Motif</th>
              <th className="px-3 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                Aucune entrée.
              </td></tr>
            )}
            {filtered.map((i) => (
              <tr key={i.id} data-testid={`pa-audit-row-${i.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{(i.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2">
                  <div className="text-xs">{i.staff_email}</div>
                  <StatusBadge tone={i.staff_role === "founder" ? "accent" : "info"}>{i.staff_role}</StatusBadge>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{i.action}</td>
                <td className="px-3 py-2 font-mono text-xs">{i.target_organization_id || <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3 py-2 text-xs max-w-xs truncate" title={i.reason}>
                  {i.reason || <span className="text-muted-foreground italic">Automatique</span>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{i.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DiagnosticsTab() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/platform-admin/email-diagnostic");
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de charger le diagnostic");
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const run = async (e) => {
    e.preventDefault();
    if (!to.includes("@")) { toast.error("Adresse e-mail invalide"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/platform-admin/email-diagnostic", { to });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Diagnostic impossible");
    } finally {
      setSending(false);
    }
  };

  const statusOk = result?.result?.status === "sent";
  const statusLogged = result?.result?.status === "logged";
  const statusErr = result && !statusOk && !statusLogged;

  return (
    <div className="max-w-3xl space-y-4">
      <ConfigStatusPanel status={status} loading={statusLoading} onRefresh={loadStatus} />

      <div className="card-elevated p-6">
        <div className="text-overline">Envoi d&apos;e-mail (production)</div>
        <h2 className="mt-1 font-heading text-xl flex items-center gap-2">
          <Mail className="h-5 w-5" /> Envoyer un e-mail de test
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Envoie un e-mail avec l&apos;émetteur officiel <code className="font-mono">{status?.sender_transactional || "Fonbex <contact@fonbex.fr>"}</code>.
          Utilisez une adresse externe (Gmail, Outlook…) pour vérifier la réception dans la boîte de réception
          (et non dans les spams). Cette action est journalisée.
        </p>
        <form onSubmit={run} className="mt-4 flex flex-col sm:flex-row gap-3">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)}
                 placeholder="adresse@exemple.com"
                 data-testid="pa-diag-to" className="flex-1" required />
          <Button type="submit" disabled={sending} data-testid="pa-diag-send">
            {sending ? "Envoi…" : "Envoyer le test"}
          </Button>
        </form>
      </div>

      {result && (
        <div className="card-elevated p-6 space-y-3" data-testid="pa-diag-result">
          <div className="flex items-center gap-2">
            {statusOk ? (
              <><CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span className="font-medium text-emerald-700">Envoi confirmé par Resend</span></>
            ) : statusLogged ? (
              <><AlertTriangle className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-700">Mode dev — e-mail journalisé (clé absente)</span></>
            ) : (
              <><XCircle className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-700">Échec de livraison</span></>
            )}
          </div>
          <div className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Destinataire</span><span className="font-mono">{result.to}</span>
            <span className="text-muted-foreground">Émetteur</span><span className="font-mono">{result.sender}</span>
            <span className="text-muted-foreground">Reply-to</span><span className="font-mono">{result.reply_to}</span>
            <span className="text-muted-foreground">Provider configuré</span>
            <span>{result.provider_configured ? "Oui (RESEND_API_KEY présente)" : "Non — mode dev, mail non envoyé"}</span>
            <span className="text-muted-foreground">Statut</span>
            <span className="font-mono text-xs">{result.result?.status}</span>
            {result.result?.id && (<>
              <span className="text-muted-foreground">ID Resend</span>
              <span className="font-mono text-xs">{result.result.id}</span>
            </>)}
            {result.result?.error_code && (<>
              <span className="text-muted-foreground">Code d&apos;erreur</span>
              <span className="font-mono text-xs">{result.result.error_code}</span>
            </>)}
            {result.result?.detail && (<>
              <span className="text-muted-foreground">Détail</span>
              <span className="text-xs">{result.result.detail}</span>
            </>)}
          </div>
          {statusErr && <RemediationHints code={result.result?.error_code} />}
        </div>
      )}
    </div>
  );
}

const RECORD_LABEL = { spf: "SPF", dkim: "DKIM", dmarc: "DMARC" };

function statusTone(s) {
  const v = (s || "").toLowerCase();
  if (v === "verified" || v === "ok") return { tone: "success", icon: CheckCircle2, label: "Vérifié" };
  if (v === "pending" || v === "not_started") return { tone: "warning", icon: RefreshCw, label: "En attente" };
  if (v === "failed" || v === "failure") return { tone: "danger", icon: XCircle, label: "Échec" };
  if (v === "not_configured") return { tone: "warning", icon: AlertTriangle, label: "Non configuré" };
  if (v === "not_added") return { tone: "warning", icon: AlertTriangle, label: "Non ajouté" };
  if (v === "unknown") return { tone: "neutral", icon: AlertTriangle, label: "Inconnu" };
  return { tone: "neutral", icon: AlertTriangle, label: s || "—" };
}

function ConfigStatusPanel({ status, loading, onRefresh }) {
  if (!status && loading) {
    return <div className="card-elevated p-6 text-sm text-muted-foreground">Chargement du diagnostic…</div>;
  }
  if (!status) return null;
  const keyOk = status.api_key_configured;
  const domainState = statusTone(status.domain_status);

  return (
    <div className="card-elevated p-6 space-y-4" data-testid="pa-diag-status">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-overline">Configuration Resend</div>
          <h2 className="mt-1 font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> État de la chaîne d&apos;envoi
          </h2>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} data-testid="pa-diag-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatusRow
          label="Clé API Resend"
          value={keyOk ? "Configurée" : "Absente"}
          tone={keyOk ? "success" : "danger"}
          Icon={keyOk ? CheckCircle2 : XCircle}
          testid="pa-diag-key"
        />
        <StatusRow
          label={`Domaine ${status.expected_domain}`}
          value={domainState.label}
          tone={domainState.tone}
          Icon={domainState.icon}
          testid="pa-diag-domain"
        />
        {Object.entries(status.records || {}).map(([k, v]) => {
          const s = statusTone(v);
          return (
            <StatusRow
              key={k}
              label={RECORD_LABEL[k] || k.toUpperCase()}
              value={s.label}
              tone={s.tone}
              Icon={s.icon}
              testid={`pa-diag-record-${k}`}
            />
          );
        })}
      </div>

      <div className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 pt-2 border-t">
        <span className="text-muted-foreground">Expéditeur transactionnel</span>
        <span className="font-mono text-xs">{status.sender_transactional}</span>
        <span className="text-muted-foreground">Contact</span>
        <span className="font-mono text-xs">{status.sender_contact}</span>
        <span className="text-muted-foreground">Support</span>
        <span className="font-mono text-xs">{status.sender_support}</span>
      </div>

      {status.error && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" />
            {status.error.detail}
          </div>
          <RemediationHints code={status.error.error_code} />
        </div>
      )}

      {status.domains?.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">Détail des domaines Resend ({status.domains.length})</summary>
          <ul className="mt-2 space-y-1 font-mono">
            {status.domains.map((d) => (
              <li key={d.id}>{d.name} — <em>{d.status}</em> — {d.region || "?"}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function StatusRow({ label, value, tone, Icon, testid }) {
  const toneCls = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-red-50 text-red-700 border-red-200",
    neutral: "bg-secondary/40 text-muted-foreground border-border",
  }[tone] || "bg-secondary/40 text-muted-foreground border-border";
  return (
    <div className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${toneCls}`} data-testid={testid}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
        <div className="text-sm font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

const REMEDIATIONS = {
  missing_api_key: [
    "Créez une clé API sur https://resend.com/api-keys",
    "Ajoutez-la dans Emergent → Secrets sous le nom RESEND_API_KEY",
    "Republiez l'application — aucune modification de code nécessaire",
  ],
  invalid_api_key: [
    "La clé est présente mais Resend la refuse — vérifiez qu'elle n'a pas été révoquée",
    "Regénérez une clé sur https://resend.com/api-keys et mettez à jour Emergent Secrets",
  ],
  unverified_domain: [
    "Rendez-vous sur Resend → Domains → Add Domain et ajoutez fonbex.fr",
    "Publiez les enregistrements DNS fournis (SPF, DKIM, DMARC) chez votre registrar",
    "Cliquez sur 'Verify DNS Records' puis rafraîchissez ce diagnostic",
  ],
  testing_mode: [
    "Votre compte Resend est en mode test — vérifiez le domaine expéditeur pour envoyer à tout le monde",
    "En attendant, testez uniquement avec les adresses vérifiées dans Resend",
  ],
  invalid_recipient: [
    "Vérifiez le format de l'adresse (une seule adresse, sans espace)",
    "Testez avec une autre boîte connue pour exister (Gmail personnel, ...)",
  ],
  quota_exceeded: [
    "Votre quota Resend est atteint — vérifiez la page Billing",
    "Passez à un plan supérieur ou attendez la prochaine période de facturation",
  ],
  rate_limited: [
    "Trop d'envois rapprochés — patientez 30 secondes puis relancez",
  ],
  unknown_provider_error: [
    "Consultez les logs backend pour le message d'erreur Resend brut",
    "Vérifiez le statut du service sur https://resend-status.com",
  ],
};

function RemediationHints({ code }) {
  const hints = REMEDIATIONS[code];
  if (!hints) return null;
  return (
    <div className="mt-2 text-xs">
      <div className="font-medium mb-1">Actions recommandées :</div>
      <ol className="list-decimal ml-4 space-y-1">
        {hints.map((h) => <li key={h}>{h}</li>)}
      </ol>
    </div>
  );
}

/* ------------------------- Programme Bêta ------------------------- */

export function BetaTab() {
  const [participants, setParticipants] = useState([]);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notes, setNotes] = useState("");
  const [toUnenroll, setToUnenroll] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/beta/participants");
      setParticipants(data.items || []);
      setLimit(data.limit || 20);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const searchUsers = async (q) => {
    setSearchQ(q);
    if (!q || q.length < 2) { setCandidates([]); return; }
    try {
      const { data } = await api.get("/platform-admin/users/search",
        { params: { q, limit: 15 } });
      setCandidates(data.items || []);
    } catch {
      // Endpoint search optionnel — silence en cas d'absence
      setCandidates([]);
    }
  };

  const enroll = async () => {
    if (!selectedUser) return;
    setBusy(true);
    try {
      await api.post("/beta/grant", {
        user_id: selectedUser.id,
        discount_rate: 0.5,
        notes: notes.trim() || null,
      });
      toast.success(`${selectedUser.email} ajouté au programme bêta`);
      setSearchOpen(false);
      setSelectedUser(null);
      setNotes("");
      setSearchQ("");
      setCandidates([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inscription impossible");
    } finally { setBusy(false); }
  };

  const unenroll = async () => {
    if (!toUnenroll) return;
    setBusy(true);
    try {
      await api.delete(`/beta/grant/${toUnenroll.id}`);
      toast.success(`${toUnenroll.email} retiré du programme bêta`);
      setToUnenroll(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retrait impossible");
    } finally { setBusy(false); }
  };

  const remaining = Math.max(0, limit - participants.length);
  const full = participants.length >= limit;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4 flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">Programme bêta — 50 % à vie</div>
          <p className="text-muted-foreground mt-1">
            Les bêta-testeurs bénéficient automatiquement du coupon Stripe
            <code className="mx-1 font-mono">BETA_LIFETIME_50</code>
            (50 % de remise permanente) appliqué à chaque abonnement, sans code
            à saisir. Limite&nbsp;: {limit} participants.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard name="beta-count" label="Participants" value={participants.length}
                 icon={Users} tone="accent" />
        <KpiCard name="beta-remaining" label="Places restantes" value={remaining} />
        <KpiCard name="beta-limit" label="Plafond" value={limit} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {loading ? "Chargement…" : `${participants.length} participant(s) actif(s)`}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="beta-refresh">
            <RefreshCw className="h-3 w-3 mr-1" /> Rafraîchir
          </Button>
          <Button size="sm" disabled={full}
                  onClick={() => setSearchOpen(true)}
                  data-testid="beta-enroll-btn">
            <Sparkles className="h-3 w-3 mr-1" />
            {full ? "Programme complet" : "Ajouter un bêta-testeur"}
          </Button>
        </div>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2">Utilisateur</th>
              <th className="px-3 py-2">E-mail</th>
              <th className="px-3 py-2">Remise</th>
              <th className="px-3 py-2">Inscrit le</th>
              <th className="px-3 py-2">Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {participants.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                Aucun bêta-testeur pour l&apos;instant. Ajoutez le premier via le bouton ci-dessus.
              </td></tr>
            )}
            {participants.map((p) => (
              <tr key={p.id} data-testid={`beta-row-${p.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">
                    {p.first_name || ""} {p.last_name || ""}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{p.email}</td>
                <td className="px-3 py-2">
                  <StatusBadge tone="accent">
                    -{Math.round((p.discount_rate || 0.5) * 100)} % à vie
                  </StatusBadge>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {(p.enrolled_at || "").slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-xs max-w-xs truncate" title={p.notes}>
                  {p.notes || <span className="text-muted-foreground italic">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setToUnenroll(p)}
                          data-testid={`beta-unenroll-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog : sélection d'un utilisateur à inscrire */}
      <Dialog open={searchOpen} onOpenChange={(o) => {
        setSearchOpen(o);
        if (!o) { setSelectedUser(null); setNotes(""); setSearchQ(""); setCandidates([]); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter un bêta-testeur</DialogTitle>
            <DialogDescription>
              Recherchez l&apos;utilisateur par e-mail ou nom, puis validez son inscription.
              Le coupon 50 % à vie sera appliqué automatiquement à son prochain abonnement Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recherche</Label>
              <Input value={searchQ}
                     onChange={(e) => searchUsers(e.target.value)}
                     placeholder="e-mail, prénom ou nom…"
                     data-testid="beta-search-input" />
            </div>
            {candidates.length > 0 && (
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {candidates.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedUser(u)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition ${
                      selectedUser?.id === u.id ? "bg-accent/10" : ""
                    }`}
                    data-testid={`beta-candidate-${u.id}`}
                  >
                    <div className="font-medium">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                    {u.beta_program?.enrolled && (
                      <StatusBadge tone="warning" className="mt-1 text-[10px]">Déjà bêta</StatusBadge>
                    )}
                  </button>
                ))}
              </div>
            )}
            {searchQ.length >= 2 && candidates.length === 0 && (
              <div className="text-xs text-muted-foreground italic">
                Aucun utilisateur trouvé pour « {searchQ} ».
              </div>
            )}
            {selectedUser && (
              <div className="rounded-lg bg-accent/5 border border-accent/20 p-3 text-sm">
                <div className="font-medium">{selectedUser.email}</div>
                <div className="text-xs text-muted-foreground">
                  Sera enrôlé avec 50 % de remise permanente.
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Notes internes (facultatif)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Contexte, canal d&apos;acquisition, promesses…"
                        data-testid="beta-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSearchOpen(false)}>Annuler</Button>
            <Button onClick={enroll} disabled={!selectedUser || busy}
                    data-testid="beta-confirm-enroll">
              {busy ? "Enregistrement…" : "Inscrire au programme"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation retrait */}
      <AlertDialog open={!!toUnenroll} onOpenChange={(o) => !o && setToUnenroll(null)}>
        <AlertDialogContent data-testid="beta-unenroll-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer du programme bêta ?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toUnenroll?.email}</strong> perdra la remise permanente de 50 %.
              Les abonnements Stripe déjà en cours conservent la remise (Stripe gère
              indépendamment le coupon déjà appliqué), mais tout nouvel abonnement se
              fera au prix plein.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={unenroll} disabled={busy}
                               className="bg-red-600 hover:bg-red-700"
                               data-testid="beta-unenroll-confirm">
              {busy ? "Retrait…" : "Retirer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* ------------------------- Offres commerciales ------------------------- */

export function OffersTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform-admin/commercial/offer-status");
      setData(data);
      setForm(data.settings);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/platform-admin/commercial/offer-status", form);
      toast.success("Offres mises à jour");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sauvegarde impossible");
    } finally { setSaving(false); }
  };

  if (!data || !form) return <div className="text-muted-foreground">Chargement…</div>;
  const c = data.counters;
  const s = form;
  const betaRemaining = Math.max(0, s.beta_slots_limit - c.beta_enrolled);
  const launchRemaining = Math.max(0, s.launch_slots_limit - c.paid_customers);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-accent/30 bg-accent/5 p-4">
        <div className="font-semibold text-sm">Compteurs affichés sur la landing</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-card p-3 border">
            <div className="text-xs text-muted-foreground">Bêta — places restantes</div>
            <div className="text-2xl font-bold text-accent mt-1">{betaRemaining}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {c.beta_enrolled} / {s.beta_slots_limit} utilisées
            </div>
          </div>
          <div className="rounded-lg bg-card p-3 border">
            <div className="text-xs text-muted-foreground">Lancement — places restantes</div>
            <div className="text-2xl font-bold text-primary mt-1">{launchRemaining}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {c.paid_customers} / {s.launch_slots_limit} — prix affiché
              {" "}<strong>{launchRemaining > 0 ? s.launch_price_eur : s.post_launch_price_eur} €/mois</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="card-elevated p-6 space-y-5">
        <div className="font-heading text-lg">Paramètres bêta</div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Prix affiché avant remise (€/mois)</Label>
            <Input type="number" step="0.01" value={s.beta_price_eur}
                   onChange={(e) => setForm({ ...form, beta_price_eur: parseFloat(e.target.value) })}
                   data-testid="offer-beta-price" />
          </div>
          <div>
            <Label>Prix effectif après 50 % (€/mois)</Label>
            <Input type="number" step="0.01" value={s.beta_discounted_price_eur}
                   onChange={(e) => setForm({ ...form, beta_discounted_price_eur: parseFloat(e.target.value) })}
                   data-testid="offer-beta-price-discounted" />
          </div>
          <div>
            <Label>Nombre total de places bêta</Label>
            <Input type="number" value={s.beta_slots_limit}
                   onChange={(e) => setForm({ ...form, beta_slots_limit: parseInt(e.target.value) })}
                   data-testid="offer-beta-limit" />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <input type="checkbox" id="beta_open" checked={s.beta_program_open}
                   onChange={(e) => setForm({ ...form, beta_program_open: e.target.checked })} />
            <Label htmlFor="beta_open" className="cursor-pointer">Programme bêta ouvert (bouton actif sur la landing)</Label>
          </div>
        </div>
      </div>

      <div className="card-elevated p-6 space-y-5">
        <div className="font-heading text-lg">Offre de lancement (250 premiers clients)</div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label>Prix de lancement (€/mois)</Label>
            <Input type="number" step="0.01" value={s.launch_price_eur}
                   onChange={(e) => setForm({ ...form, launch_price_eur: parseFloat(e.target.value) })}
                   data-testid="offer-launch-price" />
          </div>
          <div>
            <Label>Prix standard après (€/mois)</Label>
            <Input type="number" step="0.01" value={s.post_launch_price_eur}
                   onChange={(e) => setForm({ ...form, post_launch_price_eur: parseFloat(e.target.value) })}
                   data-testid="offer-launch-price-post" />
          </div>
          <div>
            <Label>Nombre de places lancement</Label>
            <Input type="number" value={s.launch_slots_limit}
                   onChange={(e) => setForm({ ...form, launch_slots_limit: parseInt(e.target.value) })}
                   data-testid="offer-launch-limit" />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load}>Annuler</Button>
        <Button onClick={save} disabled={saving} data-testid="offer-save">
          {saving ? "Enregistrement…" : "Enregistrer les modifications"}
        </Button>
      </div>
    </div>
  );
}

